/**
 * Bank holiday visual theme mapper.
 *
 * Small, controlled helper. Maps a bank holiday name to a light
 * visual theme. The point is customer clarity ("Christmas Day"
 * feels different from "Bank Holiday"). It is NOT a full theme
 * engine and MUST NOT grow into one.
 *
 * Approved categories only (see Bank Holiday scope, Feb 2026):
 *   default       - fallback for any unknown name
 *   christmas     - "Christmas" or "Boxing Day"
 *   new_year      - "New Year"
 *   lunar_new_year- "Lunar New Year" / "Chinese New Year" / "Spring Festival"
 *   diwali        - "Diwali" / "Deepavali"
 *   eid           - "Eid al-Fitr" / "Eid al-Adha" / "Eid"
 *   national      - "National Day" / "Independence Day" / "Republic Day"
 *   labour        - "Labour Day" / "May Day"
 *
 * All matching is a plain substring test (case-insensitive). No
 * emoji, no animations, no country tables, no user customisation.
 * If a name matches multiple categories, the first hit wins in
 * the order above.
 */
import {
  CalendarDays, TreePine, Sparkles, Sun, Flame,
  Star, Flag, HardHat,
} from "lucide-react";

// Order matters. First hit wins.
const CATEGORIES = [
  {
    key: "christmas",
    needles: ["christmas", "boxing day"],
    // Subtle festive green tint on the pill.
    className: "bg-emerald-50 border-emerald-200 ring-emerald-100 text-emerald-800",
    Icon: TreePine,
  },
  {
    key: "new_year",
    // "new year" also matches "Lunar New Year" and "Chinese New Year";
    // those are earlier in the list below and win before this rule.
    needles: ["new year"],
    className: "bg-amber-50 border-amber-200 ring-amber-100 text-amber-900",
    Icon: Sparkles,
  },
  {
    key: "lunar_new_year",
    needles: ["lunar new year", "chinese new year", "spring festival"],
    className: "bg-rose-50 border-rose-200 ring-rose-100 text-rose-800",
    Icon: Star,
  },
  {
    key: "diwali",
    needles: ["diwali", "deepavali"],
    className: "bg-orange-50 border-orange-200 ring-orange-100 text-orange-800",
    Icon: Flame,
  },
  {
    key: "eid",
    needles: ["eid al-fitr", "eid al-adha", "eid"],
    className: "bg-teal-50 border-teal-200 ring-teal-100 text-teal-800",
    Icon: Sun,
  },
  {
    key: "national",
    needles: ["national day", "independence day", "republic day"],
    className: "bg-indigo-50 border-indigo-200 ring-indigo-100 text-indigo-800",
    Icon: Flag,
  },
  {
    key: "labour",
    needles: ["labour day", "labor day", "may day"],
    className: "bg-slate-50 border-slate-200 ring-slate-100 text-slate-800",
    Icon: HardHat,
  },
];

// Default fallback: sky-blue pill matching the existing bank holiday
// pill style on the roster. Every unknown holiday uses this.
const DEFAULT_THEME = {
  key: "default",
  className: "bg-sky-50 border-sky-200 ring-sky-100 text-sky-800",
  Icon: CalendarDays,
};

// Resolve `name` (may be empty / null) to a theme object.
// Category order matters -- earlier categories are more specific
// (lunar_new_year must be tested before new_year to catch
// "Lunar New Year" before the generic new_year rule).
export function resolveBankHolidayTheme(name) {
  if (!name || typeof name !== "string") return DEFAULT_THEME;
  const hay = name.toLowerCase();
  // Test order: lunar_new_year, christmas, diwali, eid, national,
  // labour, new_year (last so "Lunar New Year" doesn't match it first).
  const ORDER = [
    "lunar_new_year", "christmas", "diwali", "eid",
    "national", "labour", "new_year",
  ];
  for (const key of ORDER) {
    const cat = CATEGORIES.find((c) => c.key === key);
    if (!cat) continue;
    if (cat.needles.some((n) => hay.includes(n))) return cat;
  }
  return DEFAULT_THEME;
}

// Default theme -- also useful for the "no name" branch.
export const DEFAULT_BANK_HOLIDAY_THEME = DEFAULT_THEME;
